import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
	Divider,
	Form,
	Image
} from 'semantic-ui-react';

import { log } from '../../../../../../services/utils';

import { getDocument } from '../../../../../../api/nora/submissions';
import { getSubmissionReport } from '../../../../../../api/nora/reports';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import {
	useSubmissionAction,
	useSubmissionState
} from '../../../../../../contexts/SubmissionsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Actions from '../../../../../Shared/Actions';
import FormInput from '../../../../../Form/FormInput';
import FormTextArea from '../../../../../Form/FormTextArea';
import PageHeader from '../../../../../Shared/PageHeader';
import Table from '../../../../../Table';
import DateCell from '../../../../../Table/Cells/DateCell';
import TableRowActions from '../../../../../Table/Cells/TableRowActions';
import MultiSelectColumnFilter from '../../../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../../../Table/filterFunctions/multiSelectFilterFn';
import DocumentModal from '../../../../../DocumentModal';

import styles from '../../agencies.module.css';
import multiWordFilterFn from '../../../../../Table/filterFunctions/multiWordFilterFn';
import dateFilterFn from '../../../../../Table/filterFunctions/dateFilterFn';

const ProgramDetails = ( {
	activeIndex,
	program,
	setProgram
} ) => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const { document } = useSubmissionState();
	const { setDocument } = useSubmissionAction();
	const {
		setApiErrorAlert,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	const [ documentModal, setDocumentModal ] = useState( false );

	const handleEdit = useCallback( () => {
		history.push( {
			pathname: `/operations/programs/${ program.id }`,
			state: {
				noSidebar: true,
				text: '\u2190\u00A0\u00A0Back to Agency',
				to: {
					pathname: history.location.pathname,
					state: {
						activeIndex,
						program
					}
				}
			}
		} );
	}, [
		activeIndex,
		history,
		program
	] );

	const handleBack = useCallback( () => {
		setProgram( null );
	}, [ setProgram ] );

	const actions = useMemo( () => [
		{
			action: handleBack,
			icon: 'arrow left',
			permission: true,
			text: 'Back to Programs'
		},
		{
			action: handleEdit,
			disable: !permissions.viewPrograms.canExecute,
			icon: 'calendar alternate outline',
			permission: permissions.viewPrograms.isAvailable,
			text: 'Go To Program'
		}
	], [
		handleBack,
		handleEdit,
		permissions
	] );

	// const dueDateCell = useCallback( ( { row } ) => {
	// 	if ( row.original.dueDate === '0001-01-01T00:00:00' ) {
	// 		return <p>N/A</p>;
	// 	}

	// return <DateCell date={ row.original.dueDate } />;
	// }, [] );

	const submissionDateCell = useCallback( ( { row } ) => {
		if ( row.original.submissionDate === '0001-01-01T00:00:00' ) {
			return <p>N/A</p>;
		}

		return <DateCell date={ row.original.submissionDate } />;
	}, [] );

	const onView = useCallback( async ( { id } ) => {
		try {
			setInfobarLoader( 'Retrieving Document...' );
			const document = await getDocument( id );

			setDocument( document );
			setDocumentModal( true );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setApiErrorAlert,
		setDocument,
		setInfobarLoader
	] );

	const onDownload = useCallback( async ( { id } ) => {
		setScreenLoader( 'Retreiving Submission Report...' );

		try {
			await getSubmissionReport( id );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: true,
				text: 'View Submission'
			},
			{
				action: onDownload,
				componentProps: { color: 'pink' },
				icon: 'file pdf',
				permission: true,
				text: 'Download Submission'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDownload,
		onView
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'type',
			accessor: row => `${ row.type }`,
			Header: 'Document Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		// THIS NEEDS TO BE HERE!!!!!!!
		// {
		// 	accessor: 'dueDate',
		// 	Header: 'Due Date',
		// 	filter: dateFilterFn,
		// 	Cell: dueDateCell,
		// 	minWidth: 60
		// },
		{
			accessor: 'submissionDate',
			Header: 'Submission Date',
			filter: dateFilterFn,
			Cell: submissionDateCell,
			minWidth: 60
		},
		{
			id: 'submissionStatus',
			accessor: row => `${ row.submissionStatus.name }`,
			Header: 'Submission Status',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 120,
			maxWidth: 120
		}
	], [
		submissionDateCell,
		tableRowActionsCell
	] );

	const closeDocumentModal = useCallback( () => {
		setDocumentModal( false );
		setDocument( null );
	}, [ setDocument ] );

	return (
		<>
			{ log( 'programDetails' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<div>
					<Image
						size="small"
						src="https://react.semantic-ui.com/images/wireframe/image.png"
					/>
				</div>

				<Form autoComplete="off">
					<Form.Group>
						<FormInput
							fieldLabel="Program Name"
							name="name"
							readOnly
							transparent
							value={ program.name }
							width={ 10 }
						/>
						<FormInput
							fieldLabel="Program Type"
							name="type"
							readOnly
							value={ program.type }
							width={ 6 }
						/>
					</Form.Group>

					<Divider hidden style={ { margin: '1.5rem 0' } } />

					<Form.Group>
						<FormInput
							fieldLabel="Start Date"
							name="startDate"
							readOnly
							value={ new Date( program.startDate.replace( 'Z', '' ) ).toLocaleDateString() }
							width={ 5 }
						/>
						<FormInput
							fieldLabel="End Date"
							name="endDate"
							readOnly
							value={ new Date( program.endDate.replace( 'Z', '' ) ).toLocaleDateString() }
							width={ 5 }
						/>
						<FormInput
							fieldLabel="Program Status"
							name="submissionStatus"
							readOnly
							value={ program.submissionStatus }
							width={ 6 }
						/>
					</Form.Group>

					<Divider hidden style={ { margin: '1.5rem 0' } } />

					<Form.Group>
						<FormTextArea
							fieldLabel="Description"
							name="description"
							readOnly
							value={ program.description }
							width={ 16 }
						/>
					</Form.Group>
				</Form>
			</div>

			<Divider style={ { marginTop: '5rem' } } />

			<PageHeader
				className={ styles.tablePageHeader }
				title="Submissions"
			/>
			{ program.submissions.length
				? <Table
					columns={ columns }
					data={ program.submissions }
					showCount={ program.submissions.length > 10 }
					showEmptyRows={ false }
					showPagination={ program.submissions.length > 10 }
				/>
				: <p>No documents are available at this time.</p>
			}
			<DocumentModal
				closeModal={ closeDocumentModal }
				open={ document && documentModal }
			/>
		</>
	);
};

ProgramDetails.propTypes = {
	activeIndex: PropTypes.number,
	program: PropTypes.object.isRequired,
	setProgram: PropTypes.func.isRequired
};

export default memo( ProgramDetails );
